import React, { useState, useEffect, useCallback } from 'react'
import { translate } from 'locales'
import { alertDanger, alertSuccess } from 'utils/toast'
import useFetch, { useSWRConfig } from 'hooks/useFetch'
import { Button, Select } from 'components'
import chevronDownIcon from 'assets/icons/chevron-down-gray.svg'
import moment from 'moment'
import 'moment/locale/pt-br'
import { CalendarToday, AccessTime, Alarm } from '@mui/icons-material'

import * as S from './styles'

import { IComponentProps } from './interfaces'

import * as SchedulingServices from './schedulingServices'

import { AppointmentsSelection } from './SchedulingComponents'

const Scheduling: React.FunctionComponent<IComponentProps> = ({
  scheduleUUID,
  schedule,
  onScheduleSuccess,
  patientId,
  doctorId,
}) => {
  const { mutate } = useSWRConfig()

  const getAttendanceTimeFromSpecialty = () => {
    try {
      const userDataStorage = window.localStorage.getItem('@StarBem:user')
      if (!userDataStorage) return '30'

      const userData = JSON.parse(userDataStorage)
      if (!userData.specialties || !Array.isArray(userData.specialties)) {
        return '30'
      }

      const specialty = userData.specialties.find(
        (spec: any) => spec.id === schedule?.specialty_id
      )

      if (specialty?.attendance_time) {
        return String(specialty.attendance_time)
      }

      return '30'
    } catch (error) {
      console.error('Erro ao buscar attendance_time:', error)
      return '30'
    }
  }

  const getInitialDuration = () => {
    const specialty = schedule?.specialty_slug?.toLowerCase()

    switch (specialty) {
      case 'nutricionista':
        return '30'
      case 'psicologo':
        return '30'
      case 'clinico-geral':
        return '12'
      default:
        return '15'
    }
  }

  const { data: userCredits } = useFetch({
    url: `payments/ms/v1/appointments/plan/credits?user_id=${schedule?.user_id}&specialty_slug=${schedule?.specialty_slug}`,
    condition: !!schedule?.user_id && !!schedule?.specialty_slug,
    options: {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  })

  const [selectedDuration, setSelectedDuration] = useState(getInitialDuration())
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedTime, setSelectedTime] = useState('')
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [availableDates, setAvailableDates] = useState<string[]>([])
  const [, setLoadingDates] = useState(false)
  const [availableTimes, setAvailableTimes] = useState<any[]>([])
  const [loadingTimes, setLoadingTimes] = useState(false)
  const [openSlotInAgenda, setOpenSlotInAgenda] = useState(false)
  const [guaranteeNextAppointments, setGuaranteeNextAppointments] =
    useState(false)
  const [showHowItWorks, setShowHowItWorks] = useState(false)
  const [showAppointmentsSelection, setShowAppointmentsSelection] =
    useState(false)
  const [selectedAppointments, setSelectedAppointments] = useState<string[]>([])
  const [appointmentsList, setAppointmentsList] = useState<any[]>([])
  const [editingAppointment, setEditingAppointment] = useState<string | null>(
    null
  )
  const [editSelectedDate, setEditSelectedDate] = useState('')
  const [editSelectedTime, setEditSelectedTime] = useState('')
  const [editCurrentMonth, setEditCurrentMonth] = useState(new Date())
  const [editAvailableDates, setEditAvailableDates] = useState<string[]>([])
  const [editAvailableTimes, setEditAvailableTimes] = useState<any[]>([])
  const [loadingEditTimes, setLoadingEditTimes] = useState(false)
  const [editWeekDates, setEditWeekDates] = useState<string[]>([])
  const [pendingAppointments, setPendingAppointments] = useState<any[]>([])

  const fetchAvailableDates = async (month: string, duration: string) => {
    setLoadingDates(true)
    try {
      const dates = await SchedulingServices.fetchAvailableDates({
        month,
        duration,
        schedule: schedule || {},
      })
      setAvailableDates(dates)
    } catch (error) {
      console.error('Erro ao buscar dias disponíveis:', error)
      setAvailableDates([])
    } finally {
      setLoadingDates(false)
    }
  }

  const fetchAvailableTimes = useCallback(
    async (date: string, duration: string) => {
      setLoadingTimes(true)
      try {
        const times = await SchedulingServices.fetchAvailableTimes({
          date,
          duration,
          openSlotInAgenda,
          schedule: schedule || {},
          attendanceTime: getAttendanceTimeFromSpecialty(),
        })
        setAvailableTimes(times)
      } catch (error) {
        console.error('Erro ao buscar horários disponíveis:', error)
        setAvailableTimes([])
      } finally {
        setLoadingTimes(false)
      }
    },
    [
      openSlotInAgenda,
      schedule?.doctor_id,
      schedule?.specialty_id,
      schedule?.user_country,
      schedule?.plan_id,
      schedule?.user_time_zone,
    ]
  )

  useEffect(() => {
    const newDuration = getInitialDuration()
    setSelectedDuration(newDuration)
  }, [schedule?.specialty_slug])

  useEffect(() => {
    const totalCredits =
      (userCredits?.credits || 0) + (userCredits?.credits_additional || 0)
    if (totalCredits > 0) {
      const monthString = `${currentMonth.getFullYear()}-${(currentMonth.getMonth() + 1).toString().padStart(2, '0')}`
      fetchAvailableDates(monthString, selectedDuration)
    } else {
      setAvailableDates([])
    }
  }, [currentMonth, selectedDuration, userCredits])

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableTimes(selectedDate, selectedDuration)
    }
  }, [openSlotInAgenda, selectedDate, selectedDuration, fetchAvailableTimes])

  const fetchPendingAppointments = useCallback(async () => {
    const doctorIdValue = doctorId || schedule?.doctor_id
    const userIdValue = patientId || schedule?.user_id

    if (!doctorIdValue || !userIdValue) {
      setPendingAppointments([])
      return
    }

    const appointments = await SchedulingServices.fetchPendingAppointments({
      doctorId: doctorIdValue,
      userId: userIdValue,
    })

    setPendingAppointments(appointments)
  }, [doctorId, patientId, schedule?.doctor_id, schedule?.user_id])

  useEffect(() => {
    const doctorIdValue = doctorId || schedule?.doctor_id
    const userIdValue = patientId || schedule?.user_id

    if (!doctorIdValue || !userIdValue) {
      setPendingAppointments([])
      return
    }

    const doctorIdStr = String(doctorIdValue).trim()
    const userIdStr = String(userIdValue).trim()

    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    if (!uuidRegex.test(doctorIdStr) || !uuidRegex.test(userIdStr)) {
      setPendingAppointments([])
      return
    }

    if (doctorIdStr && userIdStr) {
      fetchPendingAppointments()
    } else {
      setPendingAppointments([])
    }
  }, [
    fetchPendingAppointments,
    doctorId,
    patientId,
    schedule?.doctor_id,
    schedule?.user_id,
  ])

  const calculateDuration = (appointment: any) => {
    const specialty = (
      appointment.specialty_slug ||
      appointment.specialty_name?.toLowerCase() ||
      schedule?.specialty_slug?.toLowerCase() ||
      ''
    ).toLowerCase()
    const quantitySlot = appointment.quantity_slot || 1

    if (
      specialty === 'psicologo' ||
      specialty === 'psicologia' ||
      specialty === 'psicólogo'
    ) {
      return quantitySlot === 1 ? 30 : 60
    } else if (
      specialty === 'clinico-geral' ||
      specialty === 'clinico geral' ||
      specialty === 'clínico geral'
    ) {
      return 12
    } else if (specialty === 'nutricionista' || specialty === 'nutri') {
      return 30
    } else {
      return quantitySlot === 1 ? 15 : 30
    }
  }

  // const handleSwitchChange = (checked: boolean) => {
  //   setOpenSlotInAgenda(checked)
  //   setSelectedDate('')
  //   setSelectedTime('')
  //   setAvailableTimes([])
  // }

  const fetchAppointmentsData = async () => {
    try {
      const appointments = await SchedulingServices.fetchAppointmentsData({
        schedule: schedule || {},
        selectedDate,
        selectedTime,
        selectedDuration,
        openSlotInAgenda,
        availableTimes,
      })
      return appointments
    } catch (error: any) {
      console.error('Erro ao buscar dados de agendamento:', error)
      alertDanger(
        error?.response?.data?.message || translate('errors.errorTry')
      )
      return []
    }
  }

  const handleSchedule = async () => {
    const appointmentsData = await fetchAppointmentsData()
    if (appointmentsData.length > 0) {
      setAppointmentsList(appointmentsData)

      // Marca automaticamente o agendamento selecionado (primeiro da lista)
      const selectedAppointment = appointmentsData.find(
        (apt: any) => apt.isSelected
      )
      if (selectedAppointment) {
        setSelectedAppointments([selectedAppointment.id])
      }

      setShowAppointmentsSelection(true)
    }
  }

  const handleScheduleSingle = async () => {
    try {
      await SchedulingServices.scheduleSingle({
        schedule: schedule || {},
        scheduleUUID: scheduleUUID || '',
        selectedDate,
        selectedTime,
        selectedDuration,
        openSlotInAgenda,
      })

      alertSuccess(translate('success.schedulingSuccess'))

      if (schedule?.user_id && schedule?.specialty_slug) {
        setTimeout(async () => {
          await mutate(
            `payments/ms/v1/appointments/plan/credits?user_id=${schedule.user_id}&specialty_slug=${schedule.specialty_slug}`
          )
        }, 2000)
      }

      if (onScheduleSuccess) {
        onScheduleSuccess()
      }

      setSelectedDate('')
      setSelectedTime('')
      setCurrentMonth(new Date())
      setAvailableDates([])
      setAvailableTimes([])
      setOpenSlotInAgenda(false)
    } catch (error: any) {
      alertDanger(
        error?.response?.data?.message || translate('errors.errorTry')
      )
      console.error('Erro ao agendar consulta:', error)
    }
  }

  const handleToggleAppointment = (appointmentId: string) => {
    setSelectedAppointments((prev) =>
      prev.includes(appointmentId)
        ? prev.filter((id) => id !== appointmentId)
        : [...prev, appointmentId]
    )
  }

  const handleEditAppointment = async (appointment: any) => {
    setEditingAppointment(appointment.id)
    setEditSelectedDate(appointment.date)
    const appointmentTime = appointment.time || appointment.slotIds?.[0] || ''
    setEditSelectedTime(appointmentTime)
    const appointmentDateObj = new Date(appointment.date)
    setEditCurrentMonth(appointmentDateObj)

    const appointmentMoment = moment(appointment.date)
    const weekStart = appointmentMoment.clone().startOf('week')
    const weekEnd = appointmentMoment.clone().endOf('week')

    try {
      const monthString = `${appointmentDateObj.getFullYear()}-${(appointmentDateObj.getMonth() + 1).toString().padStart(2, '0')}`

      const weekDates: string[] = []
      const currentWeekDate = weekStart.clone()
      while (currentWeekDate.isSameOrBefore(weekEnd)) {
        weekDates.push(currentWeekDate.format('YYYY-MM-DD'))
        currentWeekDate.add(1, 'day')
      }

      const availableWeekDates =
        await SchedulingServices.fetchEditAvailableDates({
          schedule: schedule || {},
          month: monthString,
          selectedDuration,
          weekDates,
        })

      setEditAvailableDates(availableWeekDates)
      setEditWeekDates(weekDates)

      if (appointment.date) {
        await fetchEditAvailableTimes(appointment.date, selectedDuration)
      }
    } catch (error) {
      console.error('Erro ao buscar dias disponíveis:', error)
      setEditAvailableDates([])
    }
  }

  const fetchEditAvailableTimes = async (date: string, duration: string) => {
    setLoadingEditTimes(true)
    try {
      const times = await SchedulingServices.fetchEditAvailableTimes({
        schedule: schedule || {},
        date,
        duration,
      })
      setEditAvailableTimes(times)
    } catch (error) {
      console.error('Erro ao buscar horários disponíveis:', error)
      setEditAvailableTimes([])
    } finally {
      setLoadingEditTimes(false)
    }
  }

  const handleEditDateSelect = async (dateStr: string) => {
    setEditSelectedDate(dateStr)
    setEditSelectedTime('')
    await fetchEditAvailableTimes(dateStr, selectedDuration)
  }

  const handleSaveEdit = () => {
    if (!editingAppointment || !editSelectedDate || !editSelectedTime) return

    setAppointmentsList((prev) =>
      prev.map((apt) => {
        if (apt.id === editingAppointment) {
          let newTime = editSelectedTime
          if (!editSelectedTime.includes(':')) {
            const timeSlot = editAvailableTimes.find(
              (time: any) => time?.slot_id === editSelectedTime
            )
            newTime = timeSlot?.time || editSelectedTime
          }

          return {
            ...apt,
            date: editSelectedDate,
            time: newTime,
            slotIds: editSelectedTime.includes(':')
              ? apt.slotIds || []
              : [editSelectedTime],
          }
        }
        return apt
      })
    )

    setEditingAppointment(null)
    setEditSelectedDate('')
    setEditSelectedTime('')
    setEditAvailableDates([])
    setEditAvailableTimes([])
  }

  const handleCancelEdit = () => {
    setEditingAppointment(null)
    setEditSelectedDate('')
    setEditSelectedTime('')
    setEditAvailableDates([])
    setEditAvailableTimes([])
    setEditWeekDates([])
  }

  const getEditWeekDays = (): Array<{
    day: number
    date: string
    dayName: string
  }> => {
    if (editWeekDates.length === 0) return []

    const days: Array<{ day: number; date: string; dayName: string }> = []
    editWeekDates.forEach((dateStr) => {
      const date = moment(dateStr)
      days.push({
        day: date.date(),
        date: dateStr,
        dayName: dayNames[date.day()],
      })
    })

    return days
  }

  const isEditDateAvailable = (dateStr: string) => {
    return editAvailableDates.includes(dateStr)
  }

  const handleConfirmAppointments = async () => {
    try {
      if (selectedAppointments.length === 0) {
        alertDanger(translate('errors.selectAtLeastOne'))
        return
      }

      if (
        !schedule?.user_id ||
        !schedule?.doctor_id ||
        !schedule?.specialty_id
      ) {
        alertDanger('Campos obrigatórios não encontrados')
        return
      }

      if (!scheduleUUID) {
        alertDanger('ID do último agendamento não encontrado')
        return
      }

      await SchedulingServices.confirmAppointments({
        schedule: schedule || {},
        scheduleUUID: scheduleUUID || '',
        selectedAppointments,
        appointmentsList,
        selectedDuration,
      })

      alertSuccess(translate('success.schedulingSuccess'))

      if (schedule?.user_id && schedule?.specialty_slug) {
        setTimeout(async () => {
          await mutate(
            `payments/ms/v1/appointments/plan/credits?user_id=${schedule.user_id}&specialty_slug=${schedule.specialty_slug}`
          )
        }, 2000)
      }

      if (onScheduleSuccess) {
        onScheduleSuccess()
      }

      setShowAppointmentsSelection(false)
      setSelectedAppointments([])
    } catch (error: any) {
      alertDanger(
        error?.message ||
          error?.response?.data?.message ||
          translate('errors.errorTry')
      )
      console.error('Erro ao confirmar agendamentos:', error)
    }
  }

  const getDurationOptions = () => {
    const specialty = schedule?.specialty_slug?.toLowerCase()

    switch (specialty) {
      case 'nutricionista':
        return [
          { value: '30', label: translate('sideModal.durationOptions.30min') },
        ]

      case 'psicologo':
        return [
          { value: '30', label: translate('sideModal.durationOptions.30min') },
          { value: '60', label: translate('sideModal.durationOptions.60min') },
        ]

      case 'clinico-geral':
        return [
          { value: '12', label: translate('sideModal.durationOptions.12min') },
        ]

      default:
        return [
          { value: '15', label: translate('sideModal.durationOptions.15min') },
          { value: '30', label: translate('sideModal.durationOptions.30min') },
        ]
    }
  }

  const durationOptions = getDurationOptions()

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    const days = []

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day)
    }

    return days
  }

  const monthNames = [
    translate('sideModal.monthNames.january'),
    translate('sideModal.monthNames.february'),
    translate('sideModal.monthNames.march'),
    translate('sideModal.monthNames.april'),
    translate('sideModal.monthNames.may'),
    translate('sideModal.monthNames.june'),
    translate('sideModal.monthNames.july'),
    translate('sideModal.monthNames.august'),
    translate('sideModal.monthNames.september'),
    translate('sideModal.monthNames.october'),
    translate('sideModal.monthNames.november'),
    translate('sideModal.monthNames.december'),
  ]

  const dayNames = [
    translate('sideModal.dayNames.sunday'),
    translate('sideModal.dayNames.monday'),
    translate('sideModal.dayNames.tuesday'),
    translate('sideModal.dayNames.wednesday'),
    translate('sideModal.dayNames.thursday'),
    translate('sideModal.dayNames.friday'),
    translate('sideModal.dayNames.saturday'),
  ]

  const handleDateSelect = (day: number) => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth() + 1
    const formattedDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
    setSelectedDate(formattedDate)
    setSelectedTime('')
  }

  const isHighlighted = (day: number) => {
    if (openSlotInAgenda) return false

    const today = new Date()
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const isToday =
      today.getDate() === day &&
      today.getFullYear() === year &&
      today.getMonth() === month

    const isSelected =
      selectedDate &&
      day &&
      selectedDate ===
        `${currentMonth.getFullYear()}-${(currentMonth.getMonth() + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`

    return isToday && !isSelected && !isDateAvailable(day)
  }

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev)
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1)
      } else {
        newDate.setMonth(prev.getMonth() + 1)
      }
      return newDate
    })
  }

  const isDateAvailable = (day: number) => {
    if (!day) return false
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth() + 1
    const dateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
    return availableDates.includes(dateString)
  }

  const isDateClickable = (day: number) => {
    if (!day) return false

    if (openSlotInAgenda) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const dayDate = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        day
      )
      dayDate.setHours(0, 0, 0, 0)
      return dayDate >= today
    }

    return isDateAvailable(day)
  }

  if (showAppointmentsSelection) {
    return (
      <AppointmentsSelection
        appointmentsList={appointmentsList}
        selectedAppointments={selectedAppointments}
        selectedDuration={selectedDuration}
        editingAppointment={editingAppointment}
        editSelectedDate={editSelectedDate}
        editSelectedTime={editSelectedTime}
        editAvailableDates={editAvailableDates}
        editAvailableTimes={editAvailableTimes}
        editWeekDates={editWeekDates}
        loadingEditTimes={loadingEditTimes}
        dayNames={dayNames}
        monthNames={monthNames}
        editCurrentMonth={editCurrentMonth}
        onToggleAppointment={handleToggleAppointment}
        onEditAppointment={handleEditAppointment}
        onEditDateSelect={handleEditDateSelect}
        onEditTimeSelect={setEditSelectedTime}
        onSaveEdit={handleSaveEdit}
        onCancelEdit={handleCancelEdit}
        onConfirm={handleConfirmAppointments}
        getEditWeekDays={getEditWeekDays}
        isEditDateAvailable={isEditDateAvailable}
      />
    )
  }

  return (
    <S.SchedulingContainer>
      <S.SchedulingHeader>
        {/* <S.SwitchContainer>
          <label className="switch-label">
            <input
              type="checkbox"
              className="switch-input"
              checked={openSlotInAgenda}
              onChange={(e) => handleSwitchChange(e.target.checked)}
            />
            <span className="switch-text">
              {translate('sideModal.openSlotInAgendaText')}
            </span>
          </label>
        </S.SwitchContainer> */}
        {pendingAppointments.length > 0 && (
          <S.PendingAppointmentsContainer>
            <S.PendingAppointmentsTitle>
              {translate('sideModal.pendingAppointmentsTitle')}
            </S.PendingAppointmentsTitle>
            <S.PendingAppointmentsScroll>
              {pendingAppointments.map((appointment, index) => (
                <S.PendingAppointmentCard key={index}>
                  <S.AppointmentInfoRow>
                    <S.AppointmentInfoLeft>
                      <CalendarToday className="appointment-icon" />
                      <span className="appointment-label">
                        {translate('sideModal.dateLabel')}
                      </span>
                    </S.AppointmentInfoLeft>
                    <span className="appointment-value">
                      {moment
                        .utc(
                          appointment.from_date_time ||
                            appointment.date ||
                            appointment.scheduling_date
                        )
                        .add(3, 'hours')
                        .locale('pt-br')
                        .format('ddd, D MMM YYYY')
                        .replace(/(\w{3}),/, '$1.,')}
                    </span>
                  </S.AppointmentInfoRow>
                  <S.AppointmentInfoRow>
                    <S.AppointmentInfoLeft>
                      <AccessTime className="appointment-icon" />
                      <span className="appointment-label">
                        {translate('sideModal.timeLabel')}
                      </span>
                    </S.AppointmentInfoLeft>
                    <span className="appointment-value">
                      {(() => {
                        const time = moment.utc(
                          appointment.from_date_time ||
                            appointment.start_time ||
                            appointment.time
                        )

                        const hour24 = time.hour()
                        const minute = time.minute()
                        const period = hour24 >= 12 ? 'PM' : 'AM'
                        return `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} ${period}`
                      })()}
                    </span>
                  </S.AppointmentInfoRow>
                  <S.AppointmentInfoRow>
                    <S.AppointmentInfoLeft>
                      <Alarm className="appointment-icon" />
                      <span className="appointment-label">
                        {translate('sideModal.durationLabel')}
                      </span>
                    </S.AppointmentInfoLeft>
                    <span className="appointment-value">
                      {calculateDuration(appointment)}{' '}
                      {translate('sideModal.minutes')}
                    </span>
                  </S.AppointmentInfoRow>
                </S.PendingAppointmentCard>
              ))}
            </S.PendingAppointmentsScroll>
          </S.PendingAppointmentsContainer>
        )}
        <S.InstructionText>
          {translate('sideModal.instruction')}
        </S.InstructionText>
      </S.SchedulingHeader>

      <S.SchedulingContent>
        <S.LeftSection>
          <S.CreditsInfo>
            {translate('sideModal.totalCredits')}{' '}
            <span className="credits-value">{userCredits?.credits || 0}</span>
          </S.CreditsInfo>
          <S.CreditsInfo>
            {translate('newAnalysis.totalAdvancedCredits')}{' '}
            <span className="credits-value">
              {userCredits?.credits_additional || 0}
            </span>
          </S.CreditsInfo>

          {(userCredits?.credits || 0) +
            (userCredits?.credits_additional || 0) ===
          0 ? (
            <S.NoCreditsMessage>
              {translate('newAnalysis.noCreditsAvailable')}
            </S.NoCreditsMessage>
          ) : (
            <>
              {schedule?.specialty_slug?.toLowerCase() !== 'nutricionista' &&
                schedule?.specialty_slug?.toLowerCase() !== 'clinico-geral' && (
                  <S.FormGroup>
                    <label>{translate('sideModal.selectDuration')}</label>
                    <Select
                      list={durationOptions}
                      value={selectedDuration}
                      onChange={(e: any) => setSelectedDuration(e.target.value)}
                      placeholder={translate(
                        'sideModal.selectDurationPlaceholder'
                      )}
                    />
                  </S.FormGroup>
                )}

              <S.CalendarSection>
                <label>{translate('sideModal.chooseDay')}</label>
                <div className="calendar">
                  <div className="calendar-header">
                    <button
                      className="calendar-nav"
                      onClick={() => navigateMonth('prev')}
                    >
                      &lt;
                    </button>
                    <h3 className="calendar-month">
                      {monthNames[currentMonth.getMonth()]}{' '}
                      {currentMonth.getFullYear()}
                    </h3>
                    <button
                      className="calendar-nav"
                      onClick={() => navigateMonth('next')}
                    >
                      &gt;
                    </button>
                  </div>

                  <div className="calendar-grid">
                    {dayNames.map((day, index) => (
                      <div key={index} className="calendar-day-header">
                        {day}
                      </div>
                    ))}

                    {getDaysInMonth(currentMonth).map((day, index) => (
                      <div
                        key={index}
                        className={`calendar-day ${day === null ? 'empty' : ''} ${
                          selectedDate &&
                          day &&
                          selectedDate ===
                            `${currentMonth.getFullYear()}-${(currentMonth.getMonth() + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
                            ? 'selected'
                            : ''
                        } ${day && isHighlighted(day) ? 'highlighted' : ''} ${
                          !openSlotInAgenda &&
                          day &&
                          (isDateClickable(day) || isDateAvailable(day))
                            ? 'available'
                            : ''
                        } ${
                          openSlotInAgenda && day && isDateClickable(day)
                            ? 'clickable'
                            : ''
                        }`}
                        onClick={() =>
                          day && isDateClickable(day) && handleDateSelect(day)
                        }
                      >
                        {day}
                      </div>
                    ))}
                  </div>
                </div>
              </S.CalendarSection>
            </>
          )}
        </S.LeftSection>

        {(userCredits?.credits || 0) + (userCredits?.credits_additional || 0) >
          0 && (
          <S.RightSection>
            {selectedDate && (
              <S.TimeSelection>
                <label>{translate('sideModal.chooseTime')}</label>
                {loadingTimes ? (
                  <div className="loading-times">
                    {translate('sideModal.loadingTimes')}
                  </div>
                ) : (
                  <div className="time-slots">
                    {availableTimes.length > 0 ? (
                      availableTimes.map((time: any, index: number) => {
                        const timeId = openSlotInAgenda
                          ? `${time?.start}-${time?.end}`
                          : time?.slot_id
                        const timeDisplay = openSlotInAgenda
                          ? time?.start
                          : time?.time

                        return (
                          <button
                            key={timeId || index}
                            className={`time-slot ${
                              selectedTime === timeId ? 'selected' : ''
                            }`}
                            onClick={() => setSelectedTime(timeId)}
                          >
                            {timeDisplay}
                          </button>
                        )
                      })
                    ) : (
                      <div className="no-times">
                        {translate('sideModal.noTimesAvailable')}
                      </div>
                    )}
                  </div>
                )}
              </S.TimeSelection>
            )}
          </S.RightSection>
        )}
      </S.SchedulingContent>

      {(userCredits?.credits || 0) + (userCredits?.credits_additional || 0) >
        1 && (
        <S.GuaranteeSection>
          <label className="guarantee-checkbox">
            <input
              type="checkbox"
              checked={guaranteeNextAppointments}
              onChange={(e) => setGuaranteeNextAppointments(e.target.checked)}
            />
            <span>{translate('newAnalysis.guaranteeNextAppointments')}</span>
          </label>
          <p className="guarantee-description">
            {translate('newAnalysis.guaranteeNextAppointmentsDescription')}
          </p>
          <a
            href="#"
            className="guarantee-link"
            onClick={(e) => {
              e.preventDefault()
              setShowHowItWorks(!showHowItWorks)
            }}
          >
            <span className="link-text">
              {translate('newAnalysis.understandHowItWorks')}
            </span>
            <S.FAQIcon isExpanded={showHowItWorks}>
              <img src={chevronDownIcon} alt="Expandir" />
            </S.FAQIcon>
          </a>
          {showHowItWorks && (
            <S.HowItWorksContent>
              <S.HowItWorksItem>
                <S.HowItWorksQuestion>
                  {translate('newAnalysis.howItWorksQuestion')}
                </S.HowItWorksQuestion>
                <S.HowItWorksAnswer>
                  {translate('newAnalysis.howItWorksAnswer')}
                </S.HowItWorksAnswer>
              </S.HowItWorksItem>
              <S.HowItWorksItem>
                <S.HowItWorksQuestion>
                  {translate('newAnalysis.howManyAppointmentsQuestion')}
                </S.HowItWorksQuestion>
                <S.HowItWorksAnswer>
                  {translate('newAnalysis.howManyAppointmentsAnswer')}
                </S.HowItWorksAnswer>
              </S.HowItWorksItem>
              <S.HowItWorksItem>
                <S.HowItWorksQuestion>
                  {translate('newAnalysis.canPatientCancelQuestion')}
                </S.HowItWorksQuestion>
                <S.HowItWorksAnswer>
                  {translate('newAnalysis.canPatientCancelAnswer')}
                </S.HowItWorksAnswer>
              </S.HowItWorksItem>
            </S.HowItWorksContent>
          )}
        </S.GuaranteeSection>
      )}

      <S.ActionButtons>
        <Button
          color="primary"
          type="button"
          onClick={
            guaranteeNextAppointments ? handleSchedule : handleScheduleSingle
          }
          disabled={!selectedDate || !selectedTime}
        >
          {guaranteeNextAppointments
            ? translate('newAnalysis.next')
            : translate('sideModal.schedule')}
        </Button>
      </S.ActionButtons>
    </S.SchedulingContainer>
  )
}

export default Scheduling
